import { resolveTagBadgeColourSwatch } from '@/shared/data/tagBadgeColours';
import { componentSizes } from '@/theme/componentSizes';
import { layoutSizes } from '@/theme/layoutSizes';
import { isLightTheme } from '@/theme/semanticTokens';
import { fontFamilies, motion, r, s, typographyTokens, typeScale } from '@/theme/tokens';
import { alpha } from '@/ui/primitives/colorUtils';
import type { SxProps, Theme } from '@/ui/primitives';

type ThemeSxObject = Exclude<SxProps<Theme>, readonly unknown[] | ((theme: Theme) => unknown)>;
type ThemeSxFn = (theme: Theme) => ThemeSxObject;

export const sidebarBrandLogoSize = 32;

const betaChipBackgroundColor = resolveTagBadgeColourSwatch('red');

export const buildAppSidebarBrandRootSx =
  (collapsed: boolean): ThemeSxFn =>
  (theme) => ({
    '--sidebar-brand-height': `calc(${layoutSizes.topBar.height}px + (${s('xs')} * 2))`,
    color: theme.app.colors.text,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: collapsed ? 'center' : 'flex-start',
    gap: s('sm'),
    minHeight: 'var(--sidebar-brand-height)',
    height: 'var(--sidebar-brand-height)',
    pl: collapsed ? s('md') : s('lg'),
    pr: collapsed ? s('sm') : s('lg'),
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden',
    transition: `background-color ${motion.durations.fast} ${motion.easing.standard}, padding ${motion.durations.medium} ${motion.easing.standard}`,
    '&:focus-visible': {
      outline: `2px solid ${theme.app.state.focus}`,
      outlineOffset: -2,
    },
  });

export const appSidebarBrandLogoSx = {
  height: `${sidebarBrandLogoSize}px`,
  width: `${sidebarBrandLogoSize}px`,
  display: 'block',
} as const;

export const appSidebarBrandWordmarkGroupSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: s('lg'),
  minWidth: 0,
  overflow: 'hidden',
  opacity: 1,
  transform: 'translateX(0)',
  transition: `opacity ${motion.durations.fast} ${motion.easing.standard}, transform ${motion.durations.medium} ${motion.easing.standard}`,
} as const;

export const appSidebarBrandWordmarkTextSx = {
  fontFamily: fontFamilies.body,
  fontSize: typeScale.xl,
  lineHeight: 1,
  fontWeight: typographyTokens.bodyStrong.fontWeight,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
} as const;

export const appSidebarBrandBetaChipSx = {
  backgroundColor: (theme) =>
    isLightTheme(theme) ? alpha(betaChipBackgroundColor, 0.1) : betaChipBackgroundColor,
  border: (theme) =>
    `1px solid ${
      isLightTheme(theme) ? alpha(betaChipBackgroundColor, 0.22) : alpha('#ffffff', 0.14)
    }`,
  borderRadius: r('sm'),
  color: (theme) => (isLightTheme(theme) ? betaChipBackgroundColor : '#ffffff'),
  height: componentSizes.chips.xsHeight + 4,
  fontWeight: typographyTokens.bodyStrong.fontWeight,
  '& .MuiChip-label': {
    color: 'inherit',
    px: s('md'),
  },
  '& .MuiChip-label .MuiTypography-root': {
    color: 'inherit',
  },
  '&:hover, &.Mui-focusVisible, &:active': {
    backgroundColor: (theme) =>
      isLightTheme(theme) ? alpha(betaChipBackgroundColor, 0.14) : betaChipBackgroundColor,
  },
} as const satisfies SxProps<Theme>;
